<template>
  <div class="tracking-advertiser-form">
    <slot name="beforeForm" />

    <form @:submit.prevent="submit">
      <magic-container>
        <v-text-field
          data-cols="6"
          autofocus
          v-model="name"
          :label="$t('advertiser_name')"
          type="text"
          :error-messages="nameErrors"
          @blur="v$.name.$touch()"
        />
      </magic-container>

      <layout-section-header
        :title="$t('advertiser_domains')"
        :has-trigger="false"
      />

      <magic-container>
        <dropdowns-countries
          v-model="selectedCountries"
          :return-object="true"
        />

        <v-text-field
          v-model="domainName"
          :label="$t('main_domain')"
          type="text"
          :error-messages="domainNameErrors"
          :hide-details="domainNameErrors.length === 0"
          @keyup.enter="addDomain"
          @blur="() => isDomainNameTouched = true"
        />
      </magic-container>

      <div class="d-flex">
        <v-btn
          color="primary"
          class="ml-auto"
          :disabled="!canAddDomain || isLoading"
          variant="outlined"
          density="compact"
          @click="addDomain"
        >
          <v-icon start>add</v-icon>
          {{ $t('add_domain') }}
        </v-btn>
      </div>

      <lists-removables
        v-if="domainItems.length > 0"
        :items="domainItems"
        :on-remove="onRemove"
        class="tracking-advertiser-form__domains-list"
      >
        <template v-slot:item="scope">
          <div class="tracking-advertiser-form__domains-list-item">
            <span>
              {{
                countries.length === scope.item.countries.length ?
                $t('all_countries') :
                scope.item.countries.map((c) => c.name).join(', ')
              }}
            </span>
            {{ scope.item.domain }}
          </div>
        </template>
      </lists-removables>

      <div class="form-actions mt-8">
        <v-btn
          v-if="onCancel"
          variant="text"
          @click="cancel"
        >
          {{ cancelTriggerLabel }}
        </v-btn>

        <v-btn
          color="primary"
          variant="flat"
          :loading="isLoading"
          @click="submit"
        >
          {{ confirmTriggerLabel }}
        </v-btn>
      </div>
    </form>
  </div>
</template>

<script>
import { required } from '@vuelidate/validators';
import { isNonEmptyArr, isNonEmptyStr } from '../../../utils';
import { useVuelidate } from '@vuelidate/core'

export default {
  setup () {
    return {
      v$: useVuelidate()
    }
  },
  validations() {
    return {
      name: {
        required,
      },
    };
  },
  props: {
    cancelTriggerLabel: {
      type: String,
      default: 'Cancel',
    },
    confirmTriggerLabel: {
      type: String,
      default: 'Add advertiser',
    },
    onCancel: {
      type: Function,
      default: undefined,
    },
  },
  data() {
    return {
      name: '',
      selectedCountries: [],
      domainName: '',
      domainItems: [],
      isLoading: false,
      isDomainNameTouched: false,
    };
  },
  computed: {
    domainNameErrors() {
      if (!this.isDomainNameTouched) {
        return [];
      }
      if (!isNonEmptyStr(this.domainName)) {
        return [this.$t('domain_name_is_required')];
      }
      return [];
    },
    canAddDomain() {
      return (
        isNonEmptyArr(this.selectedCountries) &&
        isNonEmptyStr(this.domainName)
      );
    },
    canSubmitRequest() {
      const { name, domainItems, isLoading } = this;

      if (domainItems.length === 0) {
        this.$notify({
          type: 'error',
          text: this.$t('add_at_least_one_domain'),
        });
      }

      return (
        isNonEmptyStr(name) &&
        isNonEmptyArr(domainItems) &&
        !isLoading
      );
    },
    countries() {
      return this.$store.getters['users/countries'];
    },
    nameErrors() {
      return this.v$.name.$errors.map(error => {
        if (error.$validator === 'required') {
          return this.$t('name_is_required');
        }
        return error.$message;
      });
    },
  },
  methods: {
    addDomain() {
      if (!this.canAddDomain) {
        return;
      }

      // reset domain name touched
      this.isDomainNameTouched = false;

      this.domainItems.push({
        domain: this.domainName,
        countries: this.selectedCountries,
      });

      // clear fields
      this.domainName = '';
      this.selectedCountries = [];
    },
    removeSchemeAndWWW(domain) {
      return domain.replace(/^http(s?):\/\//i, "").replace(/^www[.]/i, "");
    },
    cancel() {
      this.clear();
      this.onCancel();
    },
    submit() {
      this.v$.$touch();

      if (!this.canSubmitRequest || this.v$.$error || this.v$.pending) {
        return false;
      }

      this.isLoading = true;

      return this.addAdvertiser();
    },
    clear() {
      this.v$.$reset();
      this.name = '';
      this.selectedCountries = [];
      this.domainName = '';
      this.domainItems = [];
      this.isLoading = false;
    },
    addAdvertiser() {
      const { name, domainItems } = this;

        const query = {
          name,
          domains: domainItems.map((d) => ({
            domain: this.removeSchemeAndWWW(d.domain),
            countries: d.countries.map((c) => c.code),
          })),
        };

      return this.$store.dispatch('tracking/createAdvertiser', query).then((res) => {
        this.$store.dispatch('tracking/setActiveAdvertiser', res).then(() => {
          this.$router.push({ name: 'tracking-management-advertiser', params: { id: res.id }, query: this.$route.query });
        });
        this.clear();

        return res;
      })
      .catch((err) => err)
      .finally(() => {
        this.isLoading = false;
      });
    },
    onRemove(item) {
      this.domainItems = this.domainItems.filter((d) => d.domain !== item.domain);
    },
  },
};
</script>

<style lang="scss">
@use 'sass:map';
@use '../../../assets/styles/theme' as *;

.domain-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.tracking-advertiser-form {
  &__domains-list {
    margin-top: 8px;

    .removable-item {
      border-radius: $border-radius;
      border: 1px solid map.get($colors, grey, lighten-2);
      min-height: 54px;
    }
  }

  // Removables scoped rule has (0,4,0) — this nesting gives (0,5,0) to win.
  [data-component-group='lists'][data-component='removables'] .v-list .removable-item {
    background-color: rgb(var(--v-theme-surface)) !important;
  }

  &__domains-list-item {
    display: flex;
    flex-direction: column;
    font-size: 16px;
    font-weight: 600;

    span {
      font-size: 12px;
      font-weight: 400;
      color: map.get($colors, grey, 'darken-2');
      margin-bottom: 4px;
    }
  }

  background-color: rgb(var(--v-theme-surface));
  padding: $spacing-xl;
}

.form-actions {
  display: flex;
  gap: $spacing-xl;
  justify-content: flex-end;
}
</style>
